import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { useDispatch, useSelector } from "react-redux";
import useFileUpload from "~/utils/useFileUpload";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import Config from "~/config/env";
import { getSasToken, removeToken } from "~/utils/fileUtils";
import { BlobServiceClient } from "@azure/storage-blob";

const useAttachments = () => {
  const { id } = useParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [filesData, setFilesdata] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFileAvailable, setIsFileAvailable] = useState(false);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [restoreMessage, setRstoreMessage] = useState("");
  const [showAttachmentConfirmation, setShowAttachmentConfirmation] =
    useState(false);

  const { id: userId } = useSelector((state) => state.login);
  const entityData = useSelector((state) => state.globalEntities.entity_detail);
  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { isDataAvailable, employeeDetails } = useSelector(
    (state) => state.employeeregister,
  );
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const storageAccount = Config.BLOB_STORAGE_ACCOUNT;
  const containerName = "ess";

  const getNameForFile = () => {
    const name =
      employeeDetails.preferred_name ||
      [employeeDetails?.name, employeeDetails?.last_name]
        .filter(Boolean)
        .join(" ");
    return { name };
  };

  useEffect(() => {
    fetchEmployeeAttachments();
  }, []);

  const fetchEmployeeAttachments = async () => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "EMPLOYEE" },
    });
    setFilesdata(data);
    setIsFileAvailable(data.length > 0);
    setLoading(false);
  };

  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];

  const uploadFileHandler = async (files) => {
    setIsFileUploaded(true);
    removeToken();
    try {
      setLoading(true);
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));
        tags = {
          entity: uuidToUlid(entityData?.id),
          country: entityData?.country,
          temporary_file: "false",
          employee: uuidToUlid(employeeDetails?.id),
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
          ...(file?.customYear && { customYear: file?.customYear.toString() }),
        };
        const container =
          file?.subcategory === "TAX_STATEMENTS" ? "ess" : "document-repo";
        const version = `${await getFileVersion(tags, container)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "EMPLOYEE",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const uploadData =
          file?.subcategory === "TAX_STATEMENTS"
            ? await uploadFileEss(file)
            : await uploadFile(file);
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({ url: uploadData.url, fileName: uploadData.id });
        file?.subcategory === "TAX_STATEMENTS"
          ? await updateTagEss(uploadData.id, tags, metadata, file)
          : await updateTag(uploadData.id, tags, metadata);
      }
      const res = await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
        body: { attachments },
        replace: { id: employeeDetails?.id, type: "EMPLOYEE" },
        schema: z.object({
          id: z.string().uuid(),
          type: z.enum(["EMPLOYEE"]),
        }),
      });
      if (res.status) {
        // Introduced a delay to allow time for the virus scan to complete after file upload,
        // ensuring the document list fetch reflects the latest and clean files.
        setTimeout(async () => {
          setIsFileUploaded(false);
          setFilesdata(files);
          setSuccessMessage("File uploaded successfully");
          fetchEmployeeAttachments();
        }, 2000);
      }
    } catch (e) {
      console.error(e);
      setIsFileUploaded(false);
    } finally {
      setShowUploadModal(false);
      removeToken();
    }
  };

  const handleChangePage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };
  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");
    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: {
          blobUrl: selectedFile?.blob_url,
          name: selectedFile?.name,
          ...(selectedFile?.subCategory === "TAX_STATEMENTS" && {
            container: "ess",
          }),
        },
        replace: { relatedId: id, relatedType: "EMPLOYEE" },
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        await fetchEmployeeAttachments();
        setAttachmentConfirmModal(false);
        setTemporaryMessage(`File ${selectedFile?.name} has been deleted.`);
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };
  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRstoreMessage("");
    setLoading(true);
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: {
        blobUrl: selectedFile?.blob_url,
        name: selectedFile?.name,
        ...(selectedFile?.subCategory === "TAX_STATEMENTS" && {
          container: "ess",
        }),
      },
    });
    if (status) {
      setTimeout(async () => {
        await fetchEmployeeAttachments();
        setRstoreMessage(`${selectedFile?.name} has been restored.`);
      }, 2000);
    }
  };

  const getBlockBlobClient = async (blobName, file) => {
    const customYear = file?.customYear;

    blobName = `${employeeDetails?.tenant_id}/${employeeDetails?.entity_id}/${id}/tax/${customYear}/${blobName}`;
    const BLOB_SAS_URL = `https://${storageAccount}.blob.core.windows.net?`;
    const sasToken = await getSasToken(containerName);
    const blobUrl = BLOB_SAS_URL + sasToken;
    const blobServiceClient = new BlobServiceClient(blobUrl);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    return containerClient.getBlockBlobClient(blobName);
  };

  const uploadFileEss = async (file, blobName = file.fileName) => {
    try {
      const blockBlobClient = await getBlockBlobClient(blobName, file);
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type,
        },
      });
      return {
        id: blobName,
        url: blockBlobClient.url,
      };
    } catch (error) {
      console.log(`An unexpected error occured : ${error}`);
      throw error;
    }
  };

  const updateTagEss = async (blobName, tags = {}, metadata = {}, file) => {
    const customYear = file?.customYear;

    blobName = `${employeeDetails?.tenant_id}/${employeeDetails?.entity_id}/${id}/tax/${customYear}/${blobName}`;
    try {
      await apiService(API_ROUTES.UPDATE_ATTACHMENT_TAGS, "POST", {
        body: { containerName, blobName, tags, metadata },
      });
      return true;
    } catch (err) {
      console.log("An unexpected error occured while updating tags ", err);
      throw err;
    }
  };

  return {
    state: {
      showUploadModal,
      employeeDetails,
      fileSupported,
      isFileUploaded,
      filesData,
      loading,
      first,
      rows,
      openDialog,
      isDataAvailable,
      attachmentConfirmModal,
      successMessage,
      isFileAvailable,
      isAttachmentLoader,
      temporaryMessage,
      restoreMessage,
      showAttachmentConfirmation,
      files,
      entityData,
      isAuditSidebarVisible,
    },
    setShowUploadModal,
    getNameForFile,
    uploadFileHandler,
    setOpenDialog,
    handleChangePage,
    handleDeleteDocument,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    setSuccessMessage,
    handleRestoreAttachment,
    setTemporaryMessage,
    setRstoreMessage,
    setShowAttachmentConfirmation,
    setFiles,
    setAuditSidebarVisible,
  };
};
export default useAttachments;
